import {
  type FlightCardData,
  type PriceConditions,
  type PriceConditionsRuleDebug,
  type PriceJourney,
  type PriceLuggage,
  type PriceOfferResponse,
  type PricePassengerGroup,
  type TransformedPriceResponse,
  usePriceOfferQuery,
} from '@massilia/queries'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  useIsMobile,
} from '@massilia/ui'
import { AlertTriangle, Briefcase, Luggage, RefreshCw } from 'lucide-react'

import { SessionExpiredError } from '@/components/booking/session-expired-error'
import { TicketConditions } from '@/components/booking/shared-trip-components'
import { useRestartFlightSearch } from '@/lib/restart-flight-search'
import { formatDuration } from '@/utils/format-duration'
import { PASSENGER_TYPE_LABELS_PLURAL as PASSENGER_TYPE_LABELS } from '@/utils/passenger-labels'
import { JOURNEY_TYPE_LABELS } from '@/utils/trip-labels'

import { useDebug } from './debug-context'
import { StopsIndicator } from './stops-indicator'

// ============================================================================
// Types
// ============================================================================

interface FlightConditionsModalProps {
  flight: FlightCardData
  isOpen: boolean
  onClose: () => void
  onSelectFlight?: () => void
  selectedJourneyKeys: string[]
  transactionKey: string
}

// ============================================================================
// Main Component
// ============================================================================

export function FlightConditionsModal({
  flight,
  isOpen,
  onClose,
  onSelectFlight,
  selectedJourneyKeys,
  transactionKey,
}: FlightConditionsModalProps) {
  const isMobile = useIsMobile()

  const { data, error, isPending, refetch } = usePriceOfferQuery(
    {
      journeyKeyArray: selectedJourneyKeys,
      offerKey: flight.offerKey,
      transactionKey,
    },
    { enabled: isOpen }
  )

  const showFooter = !!data && !data.error

  // Mobile → Dialog (modal)
  if (isMobile) {
    return (
      <Dialog onOpenChange={onClose} open={isOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-gray-light-200 px-6 py-4">
            <DialogTitle className="text-lg leading-5.5 font-semibold text-gray-light-900">
              Détails des billets
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6">
            <FlightConditionsContent
              data={data}
              error={error}
              isPending={isPending}
              onClose={onClose}
              onRetry={() => void refetch()}
            />
          </div>
          {showFooter && <StickyFooter onClose={onClose} onSelectFlight={onSelectFlight} />}
        </DialogContent>
      </Dialog>
    )
  }

  // Desktop → Sheet (right side)
  return (
    <Sheet onOpenChange={onClose} open={isOpen}>
      <SheetContent className="flex max-w-[50vw] flex-col gap-0 overflow-hidden p-0" side="right">
        <SheetHeader className="shrink-0 border-b border-gray-light-200 px-6 py-4">
          <SheetTitle className="text-lg leading-5.5 font-semibold text-gray-light-900">
            Détails des billets
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6">
          <FlightConditionsContent
            data={data}
            error={error}
            isPending={isPending}
            onClose={onClose}
            onRetry={() => void refetch()}
          />
        </div>
        {showFooter && <StickyFooter onClose={onClose} onSelectFlight={onSelectFlight} />}
      </SheetContent>
    </Sheet>
  )
}

// ============================================================================
// Content
// ============================================================================

function ConditionsSection({
  conditions,
  debugRule,
  infantCount,
}: {
  conditions: PriceConditions
  debugRule?: PriceConditionsRuleDebug
  infantCount?: number
}) {
  const showDebug = useDebug()

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-light-200 px-4 py-6">
      <h3 className="text-sm leading-4 font-semibold text-gray-light-900">Conditions par billet</h3>
      <TicketConditions
        conditions={conditions}
        debugRule={showDebug ? debugRule : undefined}
        infantCount={infantCount}
      />
    </div>
  )
}

// ============================================================================
// Passengers Section
// ============================================================================

function FlightConditionsContent({
  data,
  error,
  isPending,
  onClose,
  onRetry,
}: {
  data: PriceOfferResponse | undefined
  error: Error | null
  isPending: boolean
  onClose: () => void
  onRetry: () => void
}) {
  // Le hook doit être appelé avant les retours anticipés (règle des hooks) ; il ne
  // déclenche rien tant que l'action n'est pas invoquée.
  const restartFlightSearch = useRestartFlightSearch()

  if (isPending) return <LoadingState />
  if (error) return <NetworkErrorState onClose={onClose} onRetry={onRetry} />
  if (!data) return null
  // `data.error` = le cache d'offres a expiré côté serveur. Un simple reload rejouerait
  // la même URL, donc potentiellement la même recherche en cache : il faut forcer une
  // recherche neuve pour obtenir un `transactionKey` vivant.
  if (data.error)
    return (
      <SessionExpiredError
        message={data.message}
        onAction={() => {
          restartFlightSearch()
        }}
      />
    )
  return <PriceDetailsContent data={data.data} />
}

// ============================================================================
// Luggage Section
// ============================================================================

function JourneyCard({ journey }: { journey: PriceJourney }) {
  const firstSegment = journey.segments[0]
  const lastSegment = journey.segments.at(-1)

  if (!firstSegment || !lastSegment) return null

  const durationStr = formatDuration(journey.totalDuration)

  return (
    <div className="flex flex-col items-center gap-5 rounded-xl border border-gray-light-200 p-6">
      {/* Header: journey type + date, flight number, carrier logo */}
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-sm leading-4 font-semibold text-gray-light-900">
            {JOURNEY_TYPE_LABELS[journey.type]} : {journey.date}
          </span>
          <span className="text-[10px] leading-3 font-normal text-gray-light-600">
            {journey.flightNumber}
          </span>
        </div>
        {journey.carrier.logoUrl && (
          <img
            alt={journey.carrier.name}
            className="h-2.5 shrink-0 object-contain"
            src={journey.carrier.logoUrl}
          />
        )}
      </div>

      {/* Center-aligned vertical flight timeline */}
      <div className="flex flex-col items-center gap-2">
        {/* Departure */}
        <div className="flex flex-col items-center">
          <span className="text-base leading-5 font-semibold text-gray-light-900">
            {firstSegment.departure.time}
          </span>
          <span className="text-sm leading-4 font-medium text-gray-light-700">
            {firstSegment.departure.airport}
          </span>
        </div>

        {/* Vertical line */}
        <div className="h-5 w-px bg-gray-light-300" />

        {/* Stops + duration */}
        <div className="flex flex-col items-center gap-1">
          <StopsIndicator legs={journey.legs} stops={journey.stops} variant="compact" />
          <span className="text-xs leading-4 font-medium text-gray-light-700">{durationStr}</span>
        </div>

        {/* Vertical line */}
        <div className="h-5 w-px bg-gray-light-300" />

        {/* Arrival */}
        <div className="flex flex-col items-center">
          <span className="text-base leading-5 font-semibold text-gray-light-900">
            {lastSegment.arrival.time}
            {lastSegment.isNextDay && (
              <span className="ml-0.5 text-xs font-normal text-red-600">+1</span>
            )}
          </span>
          <span className="text-sm leading-4 font-medium text-gray-light-700">
            {lastSegment.arrival.airport}
          </span>
        </div>
      </div>

      {/* Arrival date if next day */}
      {lastSegment.isNextDay && (
        <p className="text-xs leading-4 font-normal text-red-600">
          Arrivée le {lastSegment.arrival.date} à {lastSegment.arrival.time}
        </p>
      )}
    </div>
  )
}

// ============================================================================
// Journey Card
// ============================================================================

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center gap-4">
        <div className="size-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        <p className="text-sm text-gray-light-600">Chargement des détails...</p>
      </div>
    </div>
  )
}

// ============================================================================
// Conditions Section
// ============================================================================

function LuggageSection({ luggage }: { luggage: PriceLuggage }) {
  return (
    <div className="flex flex-col gap-5 rounded-xl border border-gray-light-200 p-6">
      <h3 className="text-sm leading-4 font-semibold text-gray-light-900">
        Franchise bagage par passager
      </h3>

      {/* Checked bags */}
      <div className="flex items-center gap-3.5">
        <Luggage className="size-4 shrink-0 text-gray-light-600" />
        <div className="flex flex-col">
          {luggage.checkedBags.included ? (
            <>
              <span className="text-xs leading-4 font-medium text-gray-light-700">
                {luggage.checkedBags.quantity}{' '}
                {luggage.checkedBags.quantity > 1 ? 'bagages' : 'bagage'} en soute
                {luggage.checkedBags.weightKgPerBag &&
                  ` (${luggage.checkedBags.weightKgPerBag} kg/bagage)`}
              </span>
              {luggage.checkedBags.description && (
                <span className="text-[10px] leading-3 font-normal text-gray-light-600">
                  Total : {luggage.checkedBags.description}
                </span>
              )}
            </>
          ) : (
            <span className="text-xs leading-4 font-medium text-gray-light-700">
              Aucun bagage en soute inclus
            </span>
          )}
        </div>
      </div>

      {/* Cabin bag */}
      <div className="flex items-center gap-3.5">
        <Briefcase className="size-4 shrink-0 text-gray-light-600" />
        <div className="flex flex-col">
          <span className="text-xs leading-4 font-medium text-gray-light-700">
            {luggage.cabinBag.included ? (
              <>
                {luggage.cabinBag.quantity
                  ? `${luggage.cabinBag.quantity} bagage${luggage.cabinBag.quantity > 1 ? 's' : ''} cabine`
                  : 'Bagage cabine inclus'}
              </>
            ) : (
              'Pas de bagage cabine'
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

function NetworkErrorState({ onClose, onRetry }: { onClose: () => void; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-red-50">
          <AlertTriangle className="size-7 text-red-500" />
        </div>
        <div className="flex flex-col gap-1.5">
          <h3 className="text-base font-semibold text-gray-light-900">
            Impossible d&apos;afficher les détails du billet
          </h3>
          <p className="text-sm text-gray-light-600">
            Un problème temporaire est survenu. Vérifiez votre connexion et réessayez dans quelques
            instants.
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <Button onClick={onRetry} variant="outline">
            <RefreshCw className="size-4" />
            Réessayer
          </Button>
          <Button onClick={onClose} variant="ghost">
            Fermer
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sticky Footer
// ============================================================================

function PassengersSection({
  passengers,
  totalPrice,
}: {
  passengers: PricePassengerGroup[]
  totalPrice: { amount: string; currency: string }
}) {
  return (
    <div className="flex flex-col gap-5 rounded-xl border border-gray-light-200 p-6">
      <h3 className="text-sm leading-4 font-semibold text-gray-light-900">
        Passagers et tarification
      </h3>
      <div className="flex flex-col gap-3.5">
        {passengers.map((passenger) => (
          <div
            className="flex items-center justify-between text-sm leading-4 font-medium text-gray-light-900"
            key={passenger.type}
          >
            <span>{PASSENGER_TYPE_LABELS[passenger.type]}</span>
            <span>
              {passenger.count} x {Number(passenger.unitPrice)} €
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <span className="text-sm leading-4 font-medium text-gray-light-900">Total</span>
          <span className="text-lg leading-3 font-bold text-brand-500">
            {Number(totalPrice.amount)} € TTC
          </span>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// State Components
// ============================================================================

function PriceDetailsContent({ data }: { data: TransformedPriceResponse }) {
  return (
    <div className="flex flex-col gap-4">
      {/* Top row: Passengers & Luggage */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PassengersSection passengers={data.passengers} totalPrice={data.totalPrice} />
        <LuggageSection luggage={data.luggage} />
      </div>

      {/* Conditions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ConditionsSection
          conditions={data.conditions}
          debugRule={data.conditionsRuleDebug}
          infantCount={data.passengers.find((group) => group.type === 'Infant')?.count ?? 0}
        />
      </div>

      {/* Journeys */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.journeys.map((journey, index) => (
          <JourneyCard journey={journey} key={`${journey.type}-${index}`} />
        ))}
      </div>
    </div>
  )
}

function StickyFooter({
  onClose,
  onSelectFlight,
}: {
  onClose: () => void
  onSelectFlight?: () => void
}) {
  return (
    <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-light-200 bg-white p-6">
      <Button onClick={onClose} variant="outline">
        Retour
      </Button>
      <Button
        className="bg-linear-to-b from-[#ea6c31] to-[#d84a2d] text-white shadow-none hover:from-[#d85f2a] hover:to-[#c44328]"
        onClick={() => {
          onSelectFlight?.()
          onClose()
        }}
      >
        Sélectionner ce vol
      </Button>
    </div>
  )
}
